export type CrmGlossaryTermId =
  | 'meddpicc'
  | 'sku'
  | 'icp'
  | 'gtm'
  | 'pipeline'
  | 'outbound'
  | 'inbound'
  | 'convert'
  | 'champion'
  | 'lead';

export type CrmGlossaryTerm = {
  id: CrmGlossaryTermId;
  pattern: RegExp;
  label: { en: string; es: string };
  help: { en: string; es: string };
};

export type CrmGlossaryChunk = {
  text: string;
  termId?: CrmGlossaryTermId;
};

export const CRM_GLOSSARY: CrmGlossaryTerm[] = [
  {
    id: 'meddpicc',
    pattern: /MEDDPICC/g,
    label: { en: 'What MEDDPICC means', es: 'Qué significa MEDDPICC' },
    help: {
      en: 'A sales checklist. It asks: is there money, who decides, and how does the paper get signed? It is not a person\'s name.',
      es: 'Una lista de ventas. Pregunta si hay dinero, quién decide y cómo se firma. No es el nombre de una persona.',
    },
  },
  {
    id: 'sku',
    pattern: /SKUs?/g,
    label: { en: 'What SKU means', es: 'Qué significa SKU' },
    help: {
      en: 'Which product is for sale. Here that is CompliCore, CompliAdvantage, or Enterprise — like choosing which box to put on the shelf.',
      es: 'Qué producto se vende. Aquí es CompliCore, CompliAdvantage o Enterprise: como elegir qué caja poner en el estante.',
    },
  },
  {
    id: 'icp',
    pattern: /ICP/g,
    label: { en: 'What ICP means', es: 'Qué significa ICP' },
    help: {
      en: 'Ideal Customer Profile: the kind of company you want, not one person\'s name. Think industry, job title, company size, and place.',
      es: 'Perfil de cliente ideal: el tipo de empresa que quiere, no el nombre de una persona. Piense en sector, cargo, tamaño y lugar.',
    },
  },
  {
    id: 'gtm',
    pattern: /\bGTM\b/g,
    label: { en: 'What GTM means', es: 'Qué significa GTM' },
    help: {
      en: 'Go-to-market: the plan for finding buyers and selling the product.',
      es: 'Go-to-market: el plan para encontrar compradores y vender el producto.',
    },
  },
  {
    id: 'pipeline',
    pattern: /\b[Pp]ipeline\b/g,
    label: { en: 'What pipeline means', es: 'Qué significa pipeline' },
    help: {
      en: 'The row of steps a sale goes through, from first chat to a yes or a no.',
      es: 'La fila de pasos de una venta, desde la primera charla hasta un sí o un no.',
    },
  },
  {
    id: 'outbound',
    pattern: /\boutbound\b/gi,
    label: { en: 'What outbound means', es: 'Qué significa outbound' },
    help: {
      en: 'You go find the buyer (calls, email). The opposite of waiting for a website form.',
      es: 'Usted busca al comprador (llamadas, correo). Lo contrario de esperar un formulario web.',
    },
  },
  {
    id: 'inbound',
    pattern: /\binbound\b/gi,
    label: { en: 'What inbound means', es: 'Qué significa inbound' },
    help: {
      en: 'The buyer comes to you, usually by filling a form on the website.',
      es: 'El comprador llega a usted, casi siempre con un formulario en el sitio.',
    },
  },
  {
    id: 'convert',
    pattern: /\b(?:[Cc]onvert(?:ing|s|ion)?|convertir|Convierta|convierta)\b/g,
    label: { en: 'What convert means', es: 'Qué significa convertir' },
    help: {
      en: 'Turn a maybe-buyer (a lead) into a real sales card (a deal) with a company, a person, and a dollar amount.',
      es: 'Pasar un posible comprador (lead) a una ficha de venta (trato) con empresa, persona e importe.',
    },
  },
  {
    id: 'champion',
    pattern: /\bchampions?\b/gi,
    label: { en: 'What champion means', es: 'Qué significa campeón' },
    help: {
      en: 'Someone inside the buyer\'s company who wants you to win and will help the deal move. Not a sports trophy.',
      es: 'Alguien en la empresa compradora que quiere que gane y empuja el trato. No es un trofeo deportivo.',
    },
  },
  {
    id: 'lead',
    pattern: /\bleads?\b/gi,
    label: { en: 'What a lead is', es: 'Qué es un lead' },
    help: {
      en: 'A company or person who might buy later. It is a name on a list, not a sale yet.',
      es: 'Una empresa o persona que podría comprar después. Es un nombre en una lista, todavía no una venta.',
    },
  },
];

const findNextTerm = (
  text: string,
  from: number,
): { start: number; end: number; id: CrmGlossaryTermId } | null => {
  let best: { start: number; end: number; id: CrmGlossaryTermId } | null = null;
  for (const term of CRM_GLOSSARY) {
    const flags = term.pattern.flags.replace('g', '');
    const re = new RegExp(term.pattern.source, flags.includes('i') ? `${flags}g` : `${flags}g`);
    re.lastIndex = from;
    const match = re.exec(text);
    if (!match || match.index < from) continue;
    if (!best || match.index < best.start) {
      best = { start: match.index, end: match.index + match[0].length, id: term.id };
    }
  }
  return best;
};

export const annotateCrmTerms = (
  text: string,
  seen: Set<CrmGlossaryTermId> = new Set(),
): CrmGlossaryChunk[] => {
  const chunks: CrmGlossaryChunk[] = [];
  let cursor = 0;
  while (cursor < text.length) {
    const next = findNextTerm(text, cursor);
    if (!next) {
      chunks.push({ text: text.slice(cursor) });
      break;
    }
    if (next.start > cursor) {
      chunks.push({ text: text.slice(cursor, next.start) });
    }
    const word = text.slice(next.start, next.end);
    if (seen.has(next.id)) {
      chunks.push({ text: word });
    } else {
      seen.add(next.id);
      chunks.push({ text: word, termId: next.id });
    }
    cursor = next.end;
  }
  return chunks.filter((chunk) => chunk.text.length > 0);
};

export const crmGlossaryTerm = (id: CrmGlossaryTermId): CrmGlossaryTerm => {
  const term = CRM_GLOSSARY.find((row) => row.id === id);
  if (!term) {
    throw new Error(`Unknown CRM glossary term: ${id}`);
  }
  return term;
};
